import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  CalendarIcon, 
  ClockIcon, 
  ArrowDownTrayIcon,
  FunnelIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import EventsService from '../../services/eventsService';
import SportsService from '../../services/SportsService';
import { 
  format, 
  parseISO, 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval, 
  isSameMonth, 
  isSameDay, 
  isToday,
  addDays,
  isBefore,
  isAfter,
  startOfDay,
  endOfDay
} from 'date-fns';

const CalendarManagement = () => {
  const [events, setEvents] = useState([]);
  const [sports, setSports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar', 'list'

  // Sample data for fallback
  const sampleEvents = [
    {
      id: 1,
      title: 'Football Practice',
      event_type: 'practice',
      sport_id: 1,
      sport_name: 'Football',
      start_date: '2024-01-15',
      start_time: '15:30',
      end_date: '2024-01-15',
      end_time: '17:30',
      location: 'Main Field',
      is_published: true
    },
    {
      id: 2,
      title: 'Soccer Practice',
      event_type: 'practice',
      sport_id: 3,
      sport_name: 'Soccer',
      start_date: '2024-01-15',
      start_time: '16:00',
      end_date: '2024-01-15',
      end_time: '18:00',
      location: 'Main Field', // Same location, overlapping time
      is_published: true
    },
    {
      id: 3,
      title: 'Basketball Game vs. Riverside',
      event_type: 'game',
      sport_id: 2,
      sport_name: 'Basketball',
      start_date: '2024-01-20',
      start_time: '19:00',
      end_date: '2024-01-20',
      end_time: '21:00',
      location: 'Gymnasium',
      opponent: 'Riverside High',
      is_home: true,
      is_published: true
    }
  ];

  // Fetch sports from API
  const fetchSports = async () => {
    try {
      const response = await SportsService.getAllSports({ includeInactive: false });
      setSports(response.sports || response || []);
    } catch (error) {
      console.error('Error fetching sports:', error);
      toast.error('Failed to load sports');
      // Fallback to sample data if API fails
      setSports([
        { id: 1, name: 'Football' },
        { id: 2, name: 'Basketball' },
        { id: 3, name: 'Soccer' },
        { id: 4, name: 'Baseball' },
        { id: 5, name: 'Tennis' }
      ]);
    }
  };

  const daysOfWeek = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' }
  ];

  useEffect(() => {
    fetchEvents();
    fetchSports();
  }, []);



  // Auto-refresh events every 30 seconds to catch new events
  useEffect(() => {
    const interval = setInterval(() => {
      fetchEvents();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    fetchEvents();
    toast.info('Calendar refreshed');
  };

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const data = await EventsService.getAllEvents({
        published: false // Get all events including unpublished ones for admin
      });
      
      // Transform the data to match the expected format
      const transformedEvents = (data.events || data || []).map(event => ({
        id: event.id,
        title: event.title,
        event_type: event.event_type || event.eventType || 'meeting',
        sport_id: event.sport_id || event.sport?.id || null,
        sport_name: event.sport_name || event.sport?.name || null,
        start_date: event.start_date || (event.startDate ? event.startDate.split('T')[0] : null),
        start_time: event.start_time || (event.startDate ? event.startDate.split('T')[1]?.substring(0, 5) : null),
        end_date: event.end_date || (event.endDate ? event.endDate.split('T')[0] : event.start_date || (event.startDate ? event.startDate.split('T')[0] : null)),
        end_time: event.end_time || (event.endDate ? event.endDate.split('T')[1]?.substring(0, 5) : null),
        location: event.location || 'TBD',
        opponent: event.opponent || null,
        is_home: event.is_home || event.isHome || false,
        is_published: event.is_published !== false && event.isPublic !== false
      }));
      
      setEvents(transformedEvents);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load events');
      // Fallback to sample data if API fails
      setEvents(sampleEvents);
      setLoading(false);
    }
  };





  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const getEventsForDay = (day) => {
    return events.filter(event => 
      isSameDay(parseISO(event.start_date), day)
    );
  };

  const exportCalendar = () => {
    const csvContent = [
      ['Title', 'Type', 'Sport', 'Date', 'Start Time', 'End Time', 'Location', 'Published'].join(','),
      ...events.map(event => [
        event.title,
        event.event_type,
        event.sport_name || '',
        event.start_date,
        event.start_time,
        event.end_time,
        event.location,
        event.is_published ? 'Yes' : 'No'
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `calendar-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Calendar rendering
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarDays = eachDayOfInterval({ start: monthStart, end: monthEnd });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Calendar Management</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage and view events in calendar and list formats
          </p>
        </div>
        
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <button
            onClick={handleRefresh}
            className="btn-secondary flex items-center space-x-2"
            title="Refresh calendar to show latest events"
          >
            <ArrowPathIcon className="h-4 w-4" />
            <span>Refresh</span>
          </button>
          
          <button
            onClick={exportCalendar}
            className="btn-secondary flex items-center space-x-2"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            <span>Export</span>
          </button>
        </div>
      </div>



      {/* View Mode Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'calendar', name: 'Calendar View', icon: CalendarIcon },
            { id: 'list', name: 'List View', icon: FunnelIcon }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setViewMode(tab.id)}
              className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                viewMode === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              <span>{tab.name}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <div className="bg-white rounded-lg shadow-sm border">
          {/* Calendar Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <button
              onClick={() => navigateMonth(-1)}
              className="btn-secondary btn-sm"
            >
              ←
            </button>
            <h2 className="text-lg font-semibold text-gray-900">
              {format(currentDate, 'MMMM yyyy')}
            </h2>
            <button
              onClick={() => navigateMonth(1)}
              className="btn-secondary btn-sm"
            >
              →
            </button>
          </div>
          
          {/* Calendar Grid */}
          <div className="p-4">
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="p-2 text-center text-sm font-medium text-gray-500">
                  {day}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map(day => {
                const dayEvents = getEventsForDay(day);
                const isCurrentMonth = isSameMonth(day, currentDate);
                const isTodayDate = isToday(day);

                
                return (
                  <div
                    key={day.toISOString()}
                    className={`min-h-[100px] p-2 border border-gray-200 ${
                      isCurrentMonth ? 'bg-white' : 'bg-gray-50'
                    } ${
                      isTodayDate ? 'ring-2 ring-primary-500' : ''
                    }`}
                  >
                    <div className={`text-sm font-medium mb-1 flex items-center justify-between ${
                      isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                    } ${
                      isTodayDate ? 'text-primary-600' : ''
                    }`}>
                      <span>{format(day, 'd')}</span>

                    </div>
                    <div className="space-y-1">
                      {dayEvents.slice(0, 3).map(event => (
                        <div
                          key={event.id}
                          className={`text-xs p-1 rounded cursor-pointer ${
                            event.event_type === 'game' ? 'bg-blue-100 text-blue-800' :
                            event.event_type === 'practice' ? 'bg-green-100 text-green-800' :
                            event.event_type === 'tournament' ? 'bg-purple-100 text-purple-800' :
                            'bg-gray-100 text-gray-800'
                          }`}
                          title={`${event.title} - ${event.start_time}`}
                        >
                          <div className="truncate font-medium">{event.title}</div>
                          <div className="truncate">{event.start_time}</div>
                        </div>
                      ))}
                      {dayEvents.length > 3 && (
                        <div className="text-xs text-gray-500">
                          +{dayEvents.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Event
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date & Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {events.map(event => (
                  <tr key={event.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`flex-shrink-0 h-2 w-2 rounded-full mr-3 ${
                          event.event_type === 'game' ? 'bg-blue-500' :
                          event.event_type === 'practice' ? 'bg-green-500' :
                          event.event_type === 'tournament' ? 'bg-purple-500' :
                          'bg-gray-500'
                        }`}></div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {event.title}
                          </div>
                          <div className="text-sm text-gray-500">
                            {event.sport_name} • {event.event_type}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {format(parseISO(event.start_date), 'MMM d, yyyy')}
                      </div>
                      <div className="text-sm text-gray-500">
                        {event.start_time} - {event.end_time}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {event.location}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        event.is_published 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {event.is_published ? 'Published' : 'Draft'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}




    </div>
  );
};

export default CalendarManagement;